import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useWebSocket } from '../contexts/WebSocketContext';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Clock,
  DollarSign,
  Activity,
  RefreshCw
} from 'lucide-react';
import StockCard from '../components/StockCard';
import MarketOverview from '../components/MarketOverview';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [marketOverview, setMarketOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { subscribeToRecommendations, isConnected } = useWebSocket();

  useEffect(() => {
    fetchRecommendations();
    fetchMarketOverview();
    
    // Subscribe to real-time updates
    if (isConnected) {
      subscribeToRecommendations();
    }

    // Listen for recommendation updates
    const handleRecommendationUpdate = (event) => {
      const updatedRec = event.detail;
      setRecommendations(prev => 
        prev.map(rec => 
          rec.ticker === updatedRec.ticker ? updatedRec : rec
        )
      );
      setLastUpdated(new Date());
    };

    window.addEventListener('recommendationUpdate', handleRecommendationUpdate);
    
    return () => {
      window.removeEventListener('recommendationUpdate', handleRecommendationUpdate);
    };
  }, [isConnected, subscribeToRecommendations]);

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get('/api/stocks/recommendations');
      setRecommendations(response.data.recommendations);
      setLastUpdated(response.data.lastUpdated ? new Date(response.data.lastUpdated) : null);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketOverview = async () => {
    try {
      const response = await axios.get('/api/stocks/market-overview');
      setMarketOverview(response.data);
    } catch (error) {
      console.error('Error fetching market overview:', error);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchRecommendations(),
      fetchMarketOverview()
    ]);
    toast.success('Data refreshed');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Today's Top 5 Day Trading Opportunities
          </h1>
          <p className="text-gray-600 mt-2">
            AI-powered stock recommendations with real-time risk assessment
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {lastUpdated && (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            </div>
          )}
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Market Overview */}
      {marketOverview && <MarketOverview data={marketOverview} />}

      {/* Risk Disclaimer Alert */}
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-warning-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-warning-800">
              Risk Disclaimer
            </h3>
            <p className="text-sm text-warning-700 mt-1">
              Day trading involves substantial risk of loss. These recommendations are for 
              informational purposes only and should not be considered as financial advice. 
              Always conduct your own research and consider your risk tolerance.
            </p>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-1">
          {recommendations.map((stock, index) => (
            <StockCard 
              key={stock.ticker} 
              stock={stock} 
              rank={index + 1}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Recommendations Available
          </h3>
          <p className="text-gray-600 mb-6">
            Market analysis is currently in progress. Check back soon for today's recommendations.
          </p>
          <button
            onClick={handleRefresh}
            className="btn-primary"
          >
            Check Again
          </button>
        </div>
      )}

      {/* Quick Stats */}
      {recommendations.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card text-center">
            <div className="flex items-center justify-center mb-2">
              <TrendingUp className="h-5 w-5 text-success-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {recommendations.filter(r => r.recommendation.action === 'BUY').length}
            </div>
            <div className="text-sm text-gray-600">Buy Signals</div>
          </div>
          
          <div className="card text-center">
            <div className="flex items-center justify-center mb-2">
              <TrendingDown className="h-5 w-5 text-danger-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {recommendations.filter(r => r.recommendation.action === 'SELL').length}
            </div>
            <div className="text-sm text-gray-600">Sell Signals</div>
          </div>
          
          <div className="card text-center">
            <div className="flex items-center justify-center mb-2">
              <AlertTriangle className="h-5 w-5 text-warning-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {(recommendations.reduce((sum, r) => sum + r.riskAssessment.overallScore, 0) / recommendations.length).toFixed(1)}
            </div>
            <div className="text-sm text-gray-600">Avg Risk Score</div>
          </div>
          
          <div className="card text-center">
            <div className="flex items-center justify-center mb-2">
              <DollarSign className="h-5 w-5 text-primary-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {(recommendations.reduce((sum, r) => sum + r.recommendation.confidence, 0) / recommendations.length).toFixed(0)}%
            </div>
            <div className="text-sm text-gray-600">Avg Confidence</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;